/**
 * useSupplierSelectionHandlers Hook
 * 협력사 선택 페이지의 이벤트 핸들러 관리
 */
import { useState, useCallback, useRef } from 'react';
import { ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";
import { supplierApi } from '../api/SupplierApi';
import { SupplierDetail, supplierDetailFromDto } from '../types/supplier.types';
import { loadFilters, saveFilters } from '@/shared/utils/filterStorage';
import { getItem } from '@/shared/utils/storage';
import { exportToExcel, getTimestampForFilename, ExcelColumn } from '@/shared/utils/excelExport';
import {
    SupplierFilters,
    FILTER_STORAGE_KEY,
    filterSuppliers,
    isValidExcelFile
} from '../lib/supplierSelectionHelpers';

interface UseSupplierSelectionHandlersParams {
    onSupplierSelect: (supplierId: string, supplierName: string) => void;
    resetPagination: () => void;
}

interface UseSupplierSelectionHandlersReturn {
    // State
    suppliers: SupplierDetail[];
    allSuppliers: SupplierDetail[];
    loading: boolean;
    uploading: boolean;
    error: boolean;
    filters: SupplierFilters;
    fileInputRef: React.RefObject<HTMLInputElement | null>;
    // Handlers
    setFilters: React.Dispatch<React.SetStateAction<SupplierFilters>>;
    fetchSuppliers: () => Promise<void>;
    handleSearch: () => void;
    handleQuickFilter: (newFilters: SupplierFilters) => void;
    handleKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    handleRowClick: (supplier: SupplierDetail) => void;
    handleUploadClick: () => void;
    handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
    handleRetry: () => void;
    handleExcelDownload: () => void;
}

export const useSupplierSelectionHandlers = ({
    onSupplierSelect,
    resetPagination
}: UseSupplierSelectionHandlersParams): UseSupplierSelectionHandlersReturn => {
    const { showToast } = useToast();
    const fileInputRef = useRef<HTMLInputElement>(null);

    // ========================================
    // State
    // ========================================
    const [suppliers, setSuppliers] = useState<SupplierDetail[]>([]);
    const [allSuppliers, setAllSuppliers] = useState<SupplierDetail[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(false);
    const [filters, setFilters] = useState<SupplierFilters>(() => {
        const saved = loadFilters<SupplierFilters>(FILTER_STORAGE_KEY);
        return saved || { supplierName: '', supplierId: '', operationStatus: '전체' };
    });

    // ========================================
    // API 함수
    // ========================================

    /**
     * 협력사 목록 조회
     * 백엔드 API가 모든 상세 정보를 한 번에 반환하므로 개별 조회 불필요
     */
    const fetchSuppliers = useCallback(async () => {
        try {
            setLoading(true);
            setError(false);

            const response = await supplierApi.getSupplierList();

            // DTO를 도메인 모델로 변환 (모든 필드 포함)
            const detailedSuppliers: SupplierDetail[] = response.suppliers.map(supplierDetailFromDto);

            // 저장된 필터가 있으면 적용
            const savedFilters = loadFilters<SupplierFilters>(FILTER_STORAGE_KEY);
            if (savedFilters) {
                const filtered = filterSuppliers(detailedSuppliers, savedFilters);
                setSuppliers(filtered);
            } else {
                setSuppliers(detailedSuppliers);
            }

            setAllSuppliers(detailedSuppliers);
        } catch {
            setError(true);
            showToast('협력사 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    // ========================================
    // Event Handlers
    // ========================================

    /**
     * 검색 처리 (클라이언트 측 필터링)
     */
    const handleSearch = useCallback(() => {
        const filtered = filterSuppliers(allSuppliers, filters);
        setSuppliers(filtered);
        resetPagination();
        saveFilters(FILTER_STORAGE_KEY, filters);
    }, [allSuppliers, filters, resetPagination]);

    /**
     * 빠른 필터 처리 (필터 변경 + 즉시 검색)
     */
    const handleQuickFilter = useCallback((newFilters: SupplierFilters) => {
        setFilters(newFilters);
        const filtered = filterSuppliers(allSuppliers, newFilters);
        setSuppliers(filtered);
        resetPagination();
        saveFilters(FILTER_STORAGE_KEY, newFilters);
    }, [allSuppliers, resetPagination]);

    /**
     * Enter 키 입력 시 검색 실행
     */
    const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    }, [handleSearch]);

    /**
     * 테이블 행 클릭 시 협력사 선택
     */
    const handleRowClick = useCallback((supplier: SupplierDetail) => {
        onSupplierSelect(supplier.id, supplier.name);
    }, [onSupplierSelect]);

    /**
     * 엑셀 업로드 버튼 클릭
     */
    const handleUploadClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    /**
     * 엑셀 파일 선택 및 업로드 처리
     */
    const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // 파일 확장자 체크
        if (!isValidExcelFile(file.name)) {
            showToast('엑셀 파일만 업로드 가능합니다.', { type: ToastType.ERROR });
            return;
        }

        setUploading(true);
        try {
            // localStorage에서 실제 사용자 ID 가져오기
            const userId = getItem<string>('USER_ID') || '';
            const result = await supplierApi.uploadGradeExcel(file, userId);

            if (result.success) {
                showToast('협력사 등급이 업로드되었습니다.', { type: ToastType.SUCCESS });
                await fetchSuppliers();
            } else {
                showToast(result.message || '등급 업로드에 실패했습니다.', { type: ToastType.ERROR });
            }
        } catch {
            showToast('등급 업로드 중 오류가 발생했습니다.', { type: ToastType.ERROR });
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    }, [showToast, fetchSuppliers]);

    /**
     * API 호출 실패 시 재시도
     */
    const handleRetry = useCallback(() => {
        fetchSuppliers();
    }, [fetchSuppliers]);

    /**
     * 엑셀 다운로드 - 현재 표시된 협력사 목록을 엑셀 파일로 다운로드
     */
    const handleExcelDownload = useCallback(() => {
        if (suppliers.length === 0) {
            showToast('다운로드할 데이터가 없습니다.', { type: ToastType.WARNING });
            return;
        }

        // 엑셀 컬럼 정의 (테이블 헤더와 동일)
        const columns: ExcelColumn<SupplierDetail>[] = [
            { header: '협력사구분', key: 'suppType' },
            { header: '등급', key: 'grade' },
            { header: '협력사명', key: 'name' },
            { header: 'DP코드', key: 'id' },
            { header: '협력사연락처', key: 'phoneNumber' },
            { header: '지점명', key: 'name' },
            { header: '요금제', key: 'ratePlan' },
            { header: '등록일자', key: 'registrationDate' },
            { header: '운영종료일자', key: 'endDate' },
            { header: '사업주명', key: 'ownerName' },
            { header: '상호명', key: 'companyName' },
            { header: '사업자등록번호', key: 'businessNumber' },
            { header: '예금주', key: 'accountHolder' },
            { header: '은행명', key: 'bankName' },
            { header: '계좌번호', key: 'accountNumber' },
            { header: '이메일주소', key: 'email' },
            { header: '사업자소재지', key: 'address' },
            { header: '업태', key: 'bizType' },
            { header: '종목', key: 'bizItem' },
            { header: '과세자', key: 'taxType' }
        ];

        const filename = `협력사현황_${getTimestampForFilename()}`;
        exportToExcel(suppliers, columns, filename);
        showToast('엑셀 파일이 다운로드되었습니다.', { type: ToastType.SUCCESS });
    }, [suppliers, showToast]);

    return {
        suppliers,
        allSuppliers,
        loading,
        uploading,
        error,
        filters,
        fileInputRef,
        setFilters,
        fetchSuppliers,
        handleSearch,
        handleQuickFilter,
        handleKeyDown,
        handleRowClick,
        handleUploadClick,
        handleFileChange,
        handleRetry,
        handleExcelDownload
    };
};
